import { Flex } from "@amzn/stencil-react-components/layout";
import { PageContainer } from "@amzn/stencil-react-components/page";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getDocUploadDropdownList,
  getDocUploadMultiDropdownList,
  isCountryWithDownloadableContentDocUpload,
} from "../../helpers/utils";
import { useGetAppConfigQuery } from "../../reduxStore/api/apiSlice";
import { useAppDispatch, useAppSelector } from "../../reduxStore/reduxHooks";
import { getConfigData, getFeatureFlag, setFeatureFlag } from "../../reduxStore/slices/configSlice";
import { getCurrentPage, setIsCountryWithDownloadableContent } from "../../reduxStore/slices/docUploadSlice";
import { getASHConfigurations, getWorkAuthConfiguration } from "../../utility/app-config-helper";
import { JobDetailsModel } from "../../utility/application-data";
import { CONFIG_FEATURE_FLAG, DOC_UPLOAD_PAGE_STATE } from "../../utility/enums/common";
import { getLocale, getCountryForDomain } from "../../utility/locale-helper";
import { DocUploadSelectOptions } from "../../utility/types/common";
import AppLoader from "../common/AppLoader";
import { ActionButtons } from "./ActionButtons";
import DocStatusCardList from "./DocStatusCards/DocStatusCardList";
import DownloadDocuments from "./DownloadDocuments/DownloadDocumentList";
import { SelectDocumentsType } from "./SelectDocumentsType";
import { SelectFileToUpload } from "./SelectFileToUpload";
import { useGetJobDetails } from "../../hooks/apis/useGetJobDetails";

export const DocUpload = () => {
  const dispatch = useAppDispatch();
  const currentPageName = useSelector(getCurrentPage);
  const { isAshConfigLoaded } = useAppSelector(getFeatureFlag);
  const { candidateData } = useAppSelector(getConfigData);

  const [documentList, setDocumentList] = useState<DocUploadSelectOptions[]>([]);
  const [parentDocumentList, setParentDocumentList] = useState<DocUploadSelectOptions[]>([]);

  const [isMultiDropdown, setIsMultiDropdown] = useState<boolean>(true);
  const [isFetchingDocumentListDropdown, setIsFetchingDocumentListDropdown] = useState(true);
  const { applicationId } = useParams<{ applicationId: string }>();
  const appConfig = useGetAppConfigQuery();

  const scheduleDetails = useGetJobDetails();

  const country = getCountryForDomain();

  const setupASHConfigs = async (scheduleDetails: JobDetailsModel, appConfig: any) => {
    const configResponse = await getASHConfigurations(applicationId, scheduleDetails, appConfig);

    if (configResponse?.successfullyLoaded) {
      dispatch(
        setFeatureFlag({
          key: CONFIG_FEATURE_FLAG.IS_ASH_CONFIG_LOADED,
          value: configResponse.successfullyLoaded,
        })
      );
    }
  };

  useEffect(() => {
    if (scheduleDetails) {
      if (!isAshConfigLoaded && appConfig) {
        setupASHConfigs(scheduleDetails, appConfig);
      } else {
        getWorkAuthConfiguration(scheduleDetails).then((workAuthorizationList) => {
          const { enabledApplicants, multiDropdown } = workAuthorizationList;
          if (multiDropdown) {
            const { parentDropdown, childrenDropdown } = getDocUploadMultiDropdownList(enabledApplicants);
            setDocumentList(childrenDropdown);
            setParentDocumentList(parentDropdown);
            setIsMultiDropdown(true);
            setIsFetchingDocumentListDropdown(false);
          } else {
            setDocumentList(getDocUploadDropdownList(enabledApplicants));
            setIsFetchingDocumentListDropdown(false);
          }
        });
      }
    }
  }, [isAshConfigLoaded, applicationId, country, scheduleDetails, appConfig]);

  // check if the countries have a downloadable contents page
  useEffect(() => {
    const country = getCountryForDomain();
    dispatch(setIsCountryWithDownloadableContent(isCountryWithDownloadableContentDocUpload(country)));
  }, []);

  const renderPage = (pageState: DOC_UPLOAD_PAGE_STATE) => {
    switch (pageState) {
      case DOC_UPLOAD_PAGE_STATE.DOWNLOAD_DOCUMENTS:
        return <DownloadDocuments />;
      case DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE:
        return (
          <>
            <SelectDocumentsType
              documentList={documentList}
              isMultiDropdown={isMultiDropdown}
              parentDocumentList={parentDocumentList}
            />
            <DocStatusCardList />
          </>
        );
      case DOC_UPLOAD_PAGE_STATE.SELECT_FILE_TO_UPLOAD:
        return <SelectFileToUpload />;
      default:
        return <></>;
    }
  };

  if (!isAshConfigLoaded || !candidateData || isFetchingDocumentListDropdown) return <AppLoader />;

  return (
    <Flex flexDirection="column" justifyContent="space-between" minHeight={"100vh"}>
      <PageContainer paddingBottom="S300" paddingTop="S300" minHeight={"80vh"}>
        {renderPage(currentPageName)}
      </PageContainer>
      <ActionButtons />
    </Flex>
  );
};
